import React from "react";
import CompareModalTableBodyRow from "properties/compare/modal/compare-modal-table-body-row";
import { useTranslation } from "react-i18next";
import { TCompare } from "properties/compare/_models/properties-favorite.model";
import { TableCell, TableRow } from "@material-ui/core";
import { formatBathroomsNumber, formatBedroomsNumber } from "_common/_utils/propertyUtils";
import { LazyLoadImage } from "react-lazy-load-image-component";
import checkCircle from "../../../assets/images/icons/checkCircle.svg";
import { CompareProps } from "properties/propertyCard/components/favorite/compare-provider";
import { COMMON_RESOURCE } from "_common/resources/BaseResourceStore";
import { parkings } from "_common/_utils/unitUtils";
import { PROPERTY_TYPE } from "properties/_models/PropertyMdl";

interface Props {
    selectedItems: CompareProps[];
}

export default function CompareModalTableBody({ selectedItems }: Props) {
    const { t } = useTranslation();
    const rows = [
        { title: t("comparePage.mainData"), content: () => null },
        {
            category: t("searchBar.filter.price"),
            content: (item: TCompare) => <>{item.price}</>,
        },
        {
            category: t("propertyPage.propertyType"),
            content: (item: TCompare) => {
                return <>{t(`property.typeLabels.${item.type}`)}</>;
            },
        },
        {
            category: t("searchBar.filter.price") + " / " + t("unitPage.surfaces"),
            content: (item: TCompare) => <>{item.priceSurface}</>,
        },
        {
            category: t("comparePage.surfaces"),
            content: (item: TCompare) => {
                if (!item.surface.length) return <>/</>;
                return (
                    <>
                        {item.surface.map((s) => (
                            <div className={"mt-1"} key={s}>
                                {s}
                            </div>
                        ))}
                    </>
                );
            },
        },
        {
            category: t("words.bedrooms"),
            content: (item: TCompare) => {
                if (item.type === PROPERTY_TYPE.land) return <>/</>;
                return (
                    <>
                        {!item.bedrooms.max
                            ? item.bedrooms.min
                            : formatBedroomsNumber(item, item.bedrooms.min, item.bedrooms.max)}
                    </>
                );
            },
        },
        {
            category: t("words.bathrooms"),
            content: (item: TCompare) => {
                if (item.type === PROPERTY_TYPE.land) return <>/</>;
                return (
                    <>
                        {!item.bathrooms.max
                            ? item.bathrooms.min
                            : formatBathroomsNumber(item.bathrooms.min, item.bathrooms.max)}
                    </>
                );
            },
        },
        {
            category: t("searchBar.filter.features"),
            content: (item: TCompare) => {
                if (!item.features?.length || item.resource === "properties") return <>/</>;
                return (
                    <>
                        {item.features.map((feature, index) => {
                            if (!feature) return null;
                            return (
                                <div key={index} className={"flex items-center text-sm"}>
                                    <div>
                                        <LazyLoadImage src={checkCircle} alt="check icon" />
                                    </div>
                                    <div className="ml-3">{feature}</div>
                                </div>
                            );
                        })}
                    </>
                );
            },
        },
        {
            category: t("searchBar.filter.propertyFeatures"),
            content: (item: TCompare) => {
                if (
                    (item.resource !== COMMON_RESOURCE.PROPERTIES && !item.parentFeatures?.length) ||
                    (item.resource == COMMON_RESOURCE.PROPERTIES && !item.features.length)
                ) {
                    return <>/</>;
                }
                return (
                    <>
                        {item[item.resource === COMMON_RESOURCE.PROPERTIES ? "features" : "parentFeatures"]!.map(
                            (feature, index) => {
                                if (!feature) return null;
                                return (
                                    <div key={index} className={"flex items-center text-sm"}>
                                        <div>
                                            <LazyLoadImage src={checkCircle} alt="check icon" />
                                        </div>
                                        <div className="ml-3">{feature}</div>
                                    </div>
                                );
                            },
                        )}
                    </>
                );
            },
        },
        {
            category: t("propertyPage.section.amenities"),
            content: (item: TCompare) => {
                if (!item.amenities.length) return <>/</>;
                return (
                    <>
                        {item.amenities.map((amenitie, index) => {
                            if (!amenitie) return null;
                            return (
                                <div key={index} className={"flex items-center text-sm"}>
                                    <div>
                                        <LazyLoadImage src={checkCircle} alt="check icon" />
                                    </div>
                                    <div className="ml-3">{amenitie}</div>
                                </div>
                            );
                        })}
                    </>
                );
            },
        },
        {
            category: t("comparePage.parkings"),
            content: (item: TCompare) => {
                if (!item.parkings?.length) return <>/</>;
                return parkings(item.parkings);
            },
        },
        {
            category: t("comparePage.views"),
            content: (item: TCompare) => {
                if (!item.views.length) return <>/</>;
                return (
                    <>
                        {item.views.map((view, index) => {
                            if (!view) return null;
                            return (
                                <div key={index} className={"flex items-center text-sm"}>
                                    <div>
                                        <LazyLoadImage src={checkCircle} alt="check icon" />
                                    </div>
                                    <div className="ml-3">{view}</div>
                                </div>
                            );
                        })}
                    </>
                );
            },
        },
        { title: t("comparePage.financialData"), content: () => null },
        {
            category: t("comparePage.fees"),
            content: (item: TCompare) => {
                if (!item.fees) return <>/</>;
                return item.fees;
            },
        },
        {
            category: t("property.amenities.closingInPesos"),
            content: (item: TCompare) => {
                if (item.closingInPesos) {
                    return t("words.yes");
                }
                return <>{t("words.no")}</>;
            },
        },
    ];

    return (
        <>
            {rows.map((row, rowIndex) =>
                row.title ? (
                    <TableRow key={rowIndex}>
                        <TableCell colSpan={selectedItems.length + 1} className="sticky left-0 bg-gray-400">
                            <div className={"text-white"}>{row.title}</div>
                        </TableCell>
                    </TableRow>
                ) : (
                    <CompareModalTableBodyRow
                        key={rowIndex}
                        category={row.category}
                        selectedItems={selectedItems}
                        content={row.content || null}
                    />
                ),
            )}
        </>
    );
}
